<template>
    <div class="container">

        <!-- Loading -->
        <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
                <span class="sr-only">Loading...</span>
            </div>
        </div>

        <template v-else-if="billing">

            <!-- INSIGHT ALERTS -->
            <div v-if="pendingInsights.length" class="mb-3">
                <b-alert
                    v-for="insight in pendingInsights"
                    :key="insight.id"
                    show
                    variant="secondary"
                    class="d-flex justify-content-between align-items-start"
                >
                    <div>
                        <strong>Billing Alert</strong>
                        <p class="mb-1 mt-1">{{ insight.message }}</p>
                        <small class="text-muted">
                            Please review and acknowledge to proceed.
                            <a v-if="planMismatch && computedPlan" v-b-modal="'plan-recommendation-modal'" class="text-primary ml-2">View recommended plan</a>
                        </small>
                        <!-- Smart action CTAs -->
                        <div v-if="insightRouteActions.length" class="mt-2">
                            <a
                                v-for="action in insightRouteActions"
                                :key="action.label"
                                :href="action.href"
                                class="btn btn-sm btn-outline-primary mr-1"
                            >
                                <i v-if="action.icon" :class="['bi', action.icon, 'mr-1']"></i>{{ action.label }}
                            </a>
                        </div>
                    </div>
                    <button
                        class="btn btn-sm btn-primary ml-3"
                        :disabled="acknowledging === insight.id"
                        @click="acknowledgeInsight(insight)"
                    >
                        <span v-if="acknowledging === insight.id">
                            <span class="spinner-border spinner-border-sm" role="status"></span>
                        </span>
                        <span v-else>Acknowledge</span>
                    </button>
                </b-alert>
            </div>

            <!-- SMS USAGE -->
            <div v-if="smsUsage" class="card shadow-lg p-3 mb-3">
                <div class="card-body">
                    <div class="d-flex justify-content-between align-items-baseline">
                        <h5 class="mb-1">SMS this month</h5>
                        <span>
                            <strong :class="smsUsage.used > smsUsage.free ? 'text-primary' : ''">{{ smsUsage.used }}</strong>
                            <span class="text-muted">/ {{ smsUsage.free }} free</span>
                        </span>
                    </div>
                    <div class="progress my-2" style="height: 6px;">
                        <div class="progress-bar bg-primary" :style="{ width: Math.min(100, smsUsage.used / smsUsage.free * 100) + '%' }"></div>
                    </div>
                    <small class="text-muted">
                        SMS sent with the Dukakit sender ID: the first {{ smsUsage.free }} each month are free, then
                        {{ convertCurrencyToShillings(smsUsage.unit_price) }} per SMS, added to your next renewal.
                        <span v-if="smsUsage.used > smsUsage.free">So far this month: <strong>{{ convertCurrencyToShillings((smsUsage.used - smsUsage.free) * smsUsage.unit_price) }}</strong>.</span>
                    </small>

                    <table v-if="pendingAddons.length" class="table table-sm table-borderless mb-0 mt-3">
                        <thead>
                            <tr class="text-muted">
                                <th>Outstanding charges</th>
                                <th class="text-right">Qty</th>
                                <th class="text-right">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="addon in pendingAddons" :key="addon.id">
                                <td>{{ addon.description }}<small v-if="addon.company && addon.company.id !== billing.company_id" class="text-muted"> &middot; {{ addon.company.name }}</small></td>
                                <td class="text-right">{{ addon.quantity }}</td>
                                <td class="text-right font-weight-bold">{{ convertCurrencyToShillings(addon.total) }}</td>
                            </tr>
                            <tr class="border-top">
                                <td colspan="2" class="text-right font-weight-bold">Added to your next payment</td>
                                <td class="text-right font-weight-bold text-primary">{{ convertCurrencyToShillings(pendingAddonsTotal) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- ADD-ONS / USAGE -->
            <div v-if="addons.length" class="card shadow-lg p-3 mb-3">
                <div class="card-body">
                    <h5 class="mb-3">Usage Add-ons — {{ currentPeriod }}</h5>
                    <table class="table table-sm table-borderless mb-0">
                        <thead>
                            <tr class="text-muted">
                                <th>Description</th>
                                <th class="text-right">Qty</th>
                                <th class="text-right">Unit</th>
                                <th class="text-right">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="addon in addons" :key="addon.id">
                                <td>{{ addon.description }}</td>
                                <td class="text-right">{{ addon.quantity }}</td>
                                <td class="text-right">{{ convertCurrencyToShillings(addon.unit_price) }}</td>
                                <td class="text-right font-weight-bold">{{ convertCurrencyToShillings(addon.total) }}</td>
                            </tr>
                            <tr class="border-top">
                                <td colspan="3" class="text-right font-weight-bold">Add-ons Total</td>
                                <td class="text-right font-weight-bold text-primary">{{ convertCurrencyToShillings(addonsTotal) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- INVOICE -->
            <div class="card shadow-lg p-3 mb-3">
                <div class="card-body">
                    <div class="d-flex justify-content-between align-items-center">
                        <div>
                            <h5 class="mb-1">Invoice</h5>
                            <p class="mb-0 text-muted">
                                <small>
                                    Download invoice for <strong>{{ currentPeriod }}</strong>
                                    (Base: {{ convertCurrencyToShillings(billing.effective_amount) }}<span v-if="addonsTotal"> + Add-ons: {{ convertCurrencyToShillings(addonsTotal) }}</span>)
                                </small>
                            </p>
                        </div>
                        <a :href="`/api/billing-invoice/${currentPeriod}`" target="_blank" class="btn btn-outline-primary btn-sm">
                            Download PDF
                        </a>
                    </div>

                    <!-- Past invoices -->
                    <div v-if="invoices.length" class="mt-3">
                        <p class="text-muted mb-2"><small>Past Invoices</small></p>
                        <a
                            v-for="inv in invoices"
                            :key="inv.id"
                            :href="`/api/billing-invoice/${inv.billing_period}`"
                            target="_blank"
                            class="btn btn-outline-secondary btn-sm mr-1 mb-1"
                        >
                            {{ inv.billing_period }}
                            <span class="badge badge-secondary ml-1">{{ inv.status }}</span>
                        </a>
                    </div>
                </div>
            </div>

            <!-- PAYMENT -->
            <div class="card shadow-lg p-4 mb-3">
                <div class="text-center">
                    <h2 class="mb-3">Pay Subscription Via Mpesa</h2>
                    <hr />
                    <h5 class="py-3 text-primary">
                        {{ convertCurrencyToShillings(amountDue) }}
                    </h5>
                    <p v-if="pendingAddonsTotal" class="text-muted mb-2">
                        <small>Includes {{ convertCurrencyToShillings(pendingAddonsTotal) }} outstanding SMS charges</small>
                    </p>
                    <p class="text-muted">
                        Current Plan: <strong>{{ billing.is_custom ? 'Custom Rate' : (billing.subscription_plan ? billing.subscription_plan.name : '—') }}</strong>
                    </p>
                    <p class="text-muted">
                        Due Date: <strong>{{ billing.due_date }}</strong>
                    </p>
                    <div>
                        <p class="text-primary">Switch to an annual plan and <b>Get 1 Month Free!</b></p>
                    </div>
                    <div class="custom-control custom-switch">
                        <input v-model="billingForm.is_annual" type="checkbox" class="custom-control-input" id="annualSwitch2">
                        <label class="custom-control-label" for="annualSwitch2">Change to Annual plan</label>
                    </div>
                </div>
                <hr />
                <div class="row justify-content-center">
                    <div class="col-md-6">
                        <form @submit.prevent="paySubscriptionViaMpesa">
                            <div class="mb-3">
                                <label class="form-label">Enter your Mpesa Number (To Prompt)</label>
                                <vue-tel-input v-on:validate="validatePhoneNumber" v-model="billingForm.phoneNumber"></vue-tel-input>
                                <small class="text-muted">You will receive a request to input your M-Pesa PIN.</small>
                                <div v-if="billingForm.validPhoneMessage" class="text-danger">
                                    <small>{{ billingForm.validPhoneMessage }}</small>
                                </div>
                            </div>
                            <button type="submit" class="btn btn-primary btn-lg w-100">Continue to Pay</button>
                        </form>
                        <p class="mt-2">
                            <a v-b-modal="'paybill-modal'" class="text-big">Use paybill instead?</a>
                        </p>
                        <p>Having a challenge? <a class="text-primary" target="_blank" href="https://wa.me/254755133133">WhatsApp us</a></p>
                    </div>
                </div>
            </div>

            <!-- TRANSACTIONS -->
            <div class="text-center">
                <h2>Recent Transactions</h2>
            </div>
            <div class="container-fluid">
                <table v-if="billingTransactions && billingTransactions.length" class="table table-striped table-responsive-sm">
                    <thead>
                        <td>Transaction ID</td>
                        <td>Amount</td>
                        <td>Phone</td>
                        <td>Status</td>
                        <td>Date</td>
                    </thead>
                    <tbody>
                        <tr v-for="tx in billingTransactions" :key="tx.id">
                            <td>{{ tx.MpesaReceiptNumber }}</td>
                            <td>{{ convertCurrencyToShillings(tx.amount) }}</td>
                            <td>{{ tx.PhoneNumber }}</td>
                            <td>
                                <span v-if="tx.MpesaReceiptNumber" class="badge badge-primary">Success</span>
                                <span v-else class="badge badge-secondary">Pending</span>
                            </td>
                            <td>{{ formatDate(tx.updated_at, 'Y-MM-DD hh:mm a') }}</td>
                        </tr>
                    </tbody>
                </table>
                <p v-else class="text-muted text-center">No transactions yet.</p>
            </div>

        </template>

        <!-- Paybill Modal -->
        <b-modal id="paybill-modal" hide-footer>
            <div class="text-center">
                <p class="text-danger" v-if="c2berror">{{ c2berror }}</p>
                <h4>Dukakit Paybill</h4>
            </div>
            <div class="text-left">
                <p>To use M-PESA Paybill, follow these steps:</p>
                <ul class="list-style">
                    <li>Select <b>M-PESA</b></li>
                    <li>Select <b>Lipa Na M-PESA</b></li>
                    <li>Select <b>Paybill</b></li>
                    <li>Enter Paybill: <b>4109900</b></li>
                    <li>Enter Account: <b>{{ billing && billing.billing_account }}</b></li>
                    <li>Enter Amount: <b>{{ billing && convertCurrencyToShillings(amountDue) }}</b> or more</li>
                    <li>Enter your <b>M-PESA PIN</b> and confirm</li>
                </ul>
                <button class="btn btn-primary btn-sm" @click="confirmMpesaC2B(billing && billing.billing_account)">Confirm Transaction</button>
                <p class="mt-2">Call - 0755-133133 / 0725-445898 <a class="text-primary" target="_blank" href="https://wa.me/254755133133">WhatsApp us for assistance</a></p>
            </div>
        </b-modal>

        <!-- Processing Modal -->
        <b-modal ref="process_mpesa" id="modal-center" no-close-on-backdrop centered hide-footer>
            <div class="text-center pt-3">
                <div v-html="statusHolder"></div>
                <hr />
                <p>Experiencing any challenge?</p>
                <p><a v-b-modal="'paybill-modal'" class="text-big">Use M-PESA paybill instead?</a></p>
            </div>
        </b-modal>

        <!-- Recommended Plan Modal (only visible when there is a mismatch) -->
        <b-modal v-if="planMismatch && computedPlan" id="plan-recommendation-modal" title="System Recommended Plan" hide-footer>
            <p class="text-muted">Based on your current usage, the system recommends the following plan:</p>
            <table class="table table-borderless mb-3">
                <tbody>
                    <tr>
                        <th>Recommended Plan</th>
                        <td><span class="badge badge-secondary">{{ computedPlan.plan_label }}</span></td>
                    </tr>
                    <tr>
                        <th>Monthly Amount</th>
                        <td><strong class="text-primary">{{ convertCurrencyToShillings(computedPlan.monthly_amount) }}</strong></td>
                    </tr>
                    <tr>
                        <th>Branches</th>
                        <td>{{ computedPlan.branch_count }}</td>
                    </tr>
                    <tr v-if="computedPlan.advanced_flags.length">
                        <th>Active Modules</th>
                        <td>{{ computedPlan.advanced_flags.map(f => flagLabel(f)).join(', ') }}</td>
                    </tr>
                </tbody>
            </table>

            <!-- Branch list — shown when more than one branch is driving the recommendation -->
            <div v-if="computedPlan.branches && computedPlan.branches.length > 1" class="mb-3">
                <p class="font-weight-bold mb-2" style="font-size:.85rem;">
                    Branches counted ({{ computedPlan.branches.length }}):
                </p>
                <ul class="list-unstyled mb-0 pl-2">
                    <li
                        v-for="(branch, idx) in computedPlan.branches"
                        :key="branch.id"
                        class="py-1 border-bottom d-flex align-items-center"
                        style="font-size:.85rem;"
                    >
                        <span class="badge badge-secondary mr-2" style="min-width:22px;">{{ idx + 1 }}</span>
                        {{ branch.name }}
                    </li>
                </ul>
                <small class="text-muted d-block mt-2">
                    If a branch listed here no longer exists, please contact support to reconcile your account.
                </small>
            </div>

            <p class="text-muted"><small>To update your billing plan, please contact support.</small></p>
        </b-modal>

    </div>
</template>

<script>
    const FLAG_LABELS = {
        has_payroll:       'Payroll',
        has_reservations:  'Reservations',
        has_subscriptions: 'Subscriptions',
        has_manufacturing: 'Manufacturing',
        has_kds:           'KDS',
        enable_shifts:     'Shifts',
        enable_tims:       'eTIMS',
    };

    const MODULE_ROUTES = {
        has_payroll:       { label: 'Payroll',         href: '/payroll',        icon: 'bi-people' },
        has_reservations:  { label: 'Reservations',    href: '/reservations',   icon: 'bi-calendar-check' },
        has_manufacturing: { label: 'Manufacturing',   href: '/manufacturing',  icon: 'bi-gear' },
        has_subscriptions: { label: 'Subscriptions',   href: '/subscriptions',  icon: 'bi-arrow-repeat' },
        enable_tims:       { label: 'eTIMS Settings',  href: '/settings/general', icon: 'bi-receipt' },
        has_kds:           { label: 'KDS Settings',    href: '/settings/general', icon: 'bi-display' },
        enable_shifts:     { label: 'Shift Settings',  href: '/settings/general', icon: 'bi-clock' },
    };

    export default {
        name: 'BillingPage',
        props: ['admin_configs'],
        computed: {
            amountDue() {
                if (!this.billing) return 0;
                const base = this.billingForm.is_annual ? this.billing.effective_amount * 11 : this.billing.effective_amount;
                return base + Number(this.pendingAddonsTotal || 0);
            },
            insightRouteActions() {
                if (!this.computedPlan || !this.planMismatch) return [];
                const actions = [];

                if (this.computedPlan.branch_count > 1) {
                    actions.push({
                        label: 'Manage Branches',
                        href:  '/settings/general?openBranches=1',
                        icon:  'bi-diagram-2',
                    });
                }

                (this.computedPlan.advanced_flags || []).forEach(flag => {
                    if (MODULE_ROUTES[flag]) actions.push(MODULE_ROUTES[flag]);
                });

                return actions;
            },
        },
        data() {
            return {
                loading: true,
                billing: null,
                computedPlan: null,
                planMismatch: null,
                pendingInsights: [],
                addons: [],
                addonsTotal: 0,
                smsUsage: null,
                pendingAddons: [],
                pendingAddonsTotal: 0,
                currentPeriod: '',
                invoices: [],
                billingTransactions: [],
                billingForm: {
                    phoneNumber: '',
                    is_annual: false,
                    validPhoneMessage: '',
                },
                c2berror: '',
                acknowledging: null,
                checkMpesaResponsePoll: null,
                statusHolder:
                    '<div class="spinner-border text-primary" role="status"><span class="sr-only">Loading...</span></div>' +
                    '<p class="mt-2">Please check your phone for the M-Pesa PIN prompt...</p>',
            };
        },
        mounted() {
            this.loadBillingState();
        },
        methods: {
            async loadBillingState() {
                this.loading = true;
                try {
                    const [subRes, txRes, invRes] = await Promise.all([
                        axios.get('/api/subscription-plan'),
                        axios.get('/api/billing-transactions'),
                        axios.get('/api/billing-invoices'),
                    ]);

                    const data = subRes.data;
                    this.billing         = data;
                    this.computedPlan    = data.computed_plan;
                    this.planMismatch    = data.plan_mismatch;
                    this.pendingInsights = data.pending_insights || [];
                    this.addons          = data.addons || [];
                    this.addonsTotal     = data.addons_total || 0;
                    this.smsUsage        = data.sms_usage || null;
                    this.pendingAddons   = data.pending_addons || [];
                    this.pendingAddonsTotal = data.pending_addons_total || 0;
                    this.currentPeriod   = data.current_period;

                    this.billingTransactions = txRes.data;
                    this.invoices            = invRes.data;
                } catch (e) {
                    this.$toasts && this.$toasts.push({ type: 'error', message: 'Failed to load billing information.' });
                } finally {
                    this.loading = false;
                }
            },

            async acknowledgeInsight(insight) {
                this.acknowledging = insight.id;
                try {
                    await axios.post(`/api/billing-insights/${insight.id}/acknowledge`);
                    this.pendingInsights = this.pendingInsights.filter(i => i.id !== insight.id);
                } catch (e) {
                    this.$toasts && this.$toasts.push({ type: 'error', message: 'Could not acknowledge insight.' });
                } finally {
                    this.acknowledging = null;
                }
            },

            paySubscriptionViaMpesa() {
                if (!this.billingForm.phoneNumber) return;

                axios.post('/api/pay-monthly-subscription', { ...this.billingForm }).then(results => {
                    if (results.status === 200) {
                        this.$refs['process_mpesa'].show();

                        this.checkMpesaResponsePoll = setInterval(() => {
                            axios.get('/api/validate-billing-transaction', { params: { bill_id: results.data.id } })
                                .then(res => {
                                    if (res.data.message === 1) {
                                        this.statusHolder =
                                            '<div class="success-checkmark"><div class="check-icon">' +
                                            '<span class="icon-line line-tip"></span>' +
                                            '<span class="icon-line line-long"></span>' +
                                            '<div class="icon-circle"></div><div class="icon-fix"></div>' +
                                            '</div></div>' +
                                            '<p class="mt-2 text-primary">Payment received — account credited!</p>';
                                        clearInterval(this.checkMpesaResponsePoll);
                                        setTimeout(() => {
                                            this.$refs['process_mpesa'].hide();
                                            window.location.href = '/dashboard';
                                        }, 3000);
                                    }
                                    if (res.data.message === 0) {
                                        this.statusHolder =
                                            '<div class="text-center">' +
                                            '<p>Payment issue: ' + res.data.description + '</p>' +
                                            '<a class="btn btn-primary" href="/billing-and-plans">Retry</a></div>';
                                        clearInterval(this.checkMpesaResponsePoll);
                                    }
                                }).catch(() => {});
                        }, 10000);
                    }
                }).catch(() => {});
            },

            async confirmMpesaC2B(account) {
                const res = await axios.get(`/api/confirm-c2b-transactions?account=${account}`).catch(() => null);
                if (res && res.data.message === 'successfull') {
                    window.location.href = '/dashboard';
                } else {
                    this.c2berror = (res && res.data.message) || 'Transaction not found yet — please try again shortly.';
                }
            },

            validatePhoneNumber(phoneNumber) {
                this.billingForm.validPhoneMessage = phoneNumber.valid ? '' : 'Enter a valid phone number';
            },

            flagLabel(flag) {
                return FLAG_LABELS[flag] || flag;
            },

            formatDate(date, format) {
                return moment(date).format(format);
            },
        },
    };
</script>

